import { useCallback } from "react";
import { useAnalytics } from "./AnalyticsProvider";
import { Kpi, type Todo } from "@acto/taskpane/core/domain";
import {
  AnalyticsEvents,
  type AnalyticsEventName,
  type AnalyticsEventPropertiesMap,
  type AnalysisMetric,
  type TodoCompletedProperties,
} from "./types";

/** Delay in ms before firing a metrics-viewed event, to capture any filter changes. */
export const ANALYTICS_TRACK_DELAY_MS = 3000;

/** Derives the analytics source value from a todo's origin flags. */
export function getTodoSource(todo: Todo): TodoCompletedProperties["source"] {
  if (todo.isWorkflow) return "workflow";
  if (todo.isNoteBased) return "recap";
  return "manual";
}

export function kpiToMetric(kpi: Kpi): AnalysisMetric {
  return kpi.toLowerCase() as AnalysisMetric;
}

/**
 * Typed event emitter: the event name selects its payload shape via `AnalyticsEventPropertiesMap`,
 * so firing an event with another event's payload is a compile error.
 */
export function useTrack() {
  return useAnalytics().track;
}

function useTrackEvent<E extends AnalyticsEventName>(event: E) {
  const track = useTrack();
  return useCallback((properties: AnalyticsEventPropertiesMap[E]) => track(event, properties), [track, event]);
}

/** @deprecated FS1 shim — call `useTrack` directly. Removed together with `taskpane/field-sales`. */
export function useTrackChat() {
  return {
    trackChatOpened: useTrackEvent(AnalyticsEvents.CHAT_OPENED),
    trackMessageSent: useTrackEvent(AnalyticsEvents.CHAT_MESSAGE_SENT),
  };
}

/** @deprecated FS1 shim — call `useTrack` directly. Removed together with `taskpane/field-sales`. */
export function useTrackCustomer() {
  return {
    trackSearch: useTrackEvent(AnalyticsEvents.CUSTOMER_SEARCH),
    trackCustomerDetailsOpened: useTrackEvent(AnalyticsEvents.CUSTOMER_DETAILS_OPENED),
    trackPlanned: useTrackEvent(AnalyticsEvents.CUSTOMER_PLANNED),
  };
}

/** @deprecated FS1 shim — call `useTrack` directly. Removed together with `taskpane/field-sales`. */
export function useTrackMeetingPrep() {
  return { trackMeetingPrepOpened: useTrackEvent(AnalyticsEvents.MEETING_PREP_OPENED) };
}

/** @deprecated FS1 shim — call `useTrack` directly. Removed together with `taskpane/field-sales`. */
export function useTrackNote() {
  return { trackNoteCreated: useTrackEvent(AnalyticsEvents.NOTE_CREATED) };
}

/** @deprecated FS1 shim — call `useTrack` directly. Removed together with `taskpane/field-sales`. */
export function useTrackRecommendedCustomer() {
  return {
    trackRecommendedCustomerClicked: useTrackEvent(AnalyticsEvents.RECOMMENDED_CUSTOMER_CLICKED),
    trackRecommendedCustomerDismissed: useTrackEvent(AnalyticsEvents.RECOMMENDED_CUSTOMER_DISMISSED),
  };
}

/** @deprecated FS1 shim — call `useTrack` directly. Removed together with `taskpane/field-sales`. */
export function useTrackTodo() {
  return { trackTodoCompleted: useTrackEvent(AnalyticsEvents.TODO_COMPLETED) };
}

/** @deprecated FS1 shim — call `useTrack` directly. Removed together with `taskpane/field-sales`. */
export function useTrackRevenueMetrics() {
  return { trackRevenueMetricsViewed: useTrackEvent(AnalyticsEvents.REVENUE_METRICS_VIEWED) };
}

/** @deprecated FS1 shim — call `useTrack` directly. Removed together with `taskpane/field-sales`. */
export function useTrackProductMetrics() {
  return { trackProductMetricsViewed: useTrackEvent(AnalyticsEvents.PRODUCT_METRICS_VIEWED) };
}
